import React, { useEffect, useRef, useState } from "react";
import "./InstrumentationMonitoring.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/im-gallery-1.jpeg";
import gallery2 from "../assets/gallery/im-gallery-2.jpeg";
import gallery3 from "../assets/gallery/im-gallery-3.jpeg";
import gallery4 from "../assets/gallery/im-gallery-4.jpeg";
import gallery5 from "../assets/gallery/im-gallery-5.jpeg";
import gallery6 from "../assets/gallery/im-gallery-6.jpeg";
import gallery7 from "../assets/gallery/im-gallery-7.jpeg";
import gallery8 from "../assets/gallery/im-gallery-8.jpeg";
import overviewImg from "../assets/gallery/im-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1},
  { src: gallery2},
  { src: gallery3},
  { src: gallery4},
  { src: gallery5},
  { src: gallery6},
  { src: gallery7},
  { src: gallery8},
];

/* ── Main InstrumentationMonitoring Component ── */
const InstrumentationMonitoring = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "Inclinometer Monitoring",
      desc: "Borehole inclinometer installations to measure lateral ground and retaining wall deformation at depth throughout excavation works.",
      icon: "📐",
    },
    {
      number: "02",
      title: "Piezometer Monitoring",
      desc: "Vibrating wire and standpipe piezometers to track groundwater levels and pore pressure changes around excavation and dewatering zones.",
      icon: "💧",
    },
    {
      number: "03",
      title: "Settlement Markers & Levelling",
      desc: "Precise optical and electronic levelling of surface and building settlement markers to detect ground movement over time.",
      icon: "📏",
    },
    {
      number: "04",
      title: "Tiltmeters & Crack Gauges",
      desc: "Structural monitoring instruments installed on adjacent buildings to detect tilt and crack propagation during nearby construction.",
      icon: "🏚",
    },
    {
      number: "05",
      title: "Vibration & Noise Monitoring",
      desc: "Vibration and noise montoring is essential to monitor the structure stability of near by bulidings and monitor the noise level to ensure it is within the allowed decibels",
      icon: "📡",
    },
    {
      number: "06",
      title: "Submission of reports",
      desc: "Submission of reports precisely with real time data to ensure and adapt latest technoloiges in the industry",
      icon: "📋",
    },
  ];

  return (
    <div className="im-page" id="#instrumentation-monitoring">
      {/* Top Banner */}
      <section className="im-top-banner">
        <div className="im-top-banner__grid" />
        <div className="im-top-banner__glow" />
        <h1 className="im-top-banner__title">
          Instrumentation <span className="im-text-accent">& Monitoring</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`im-overview ${isVisible["overview"] ? "im-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="im-overview__inner">
          <div className="im-overview__left">
            <div className="im-section-label">
              <span className="im-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="im-section-title">
              WATCHING THE
              <br />
              <span className="im-text-accent">GROUND IN MOTION</span>
            </h2>
            <p className="im-overview__text">
              Construction near sensitive structures demands constant vigilance. GSK
              Geotech's Instrumentation & Monitoring service deploys a full suite of
              geotechnical and structural instruments to track ground movement, water
              levels, and structural response in real time — giving engineers the data
              they need to manage risk proactively.
            </p>
            <p className="im-overview__text">
              Our experienced monitoring team has instrumented major excavation, tunnelling,
              and piling projects across Singapore, delivering reliable, continuous data
              with automated alerts and clear, actionable reporting.
            </p>
            <div className="im-overview__highlights">
              {[
                "Experienced instrumentation & monitoring team",
                "All types of instruments",
                "Accuracy and quality",
                "Compliant with industry & regulatory standards",
              ].map((item, i) => (
                <div key={i} className="im-overview__highlight-item">
                  <span className="im-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="im-overview__right">
            <div className="im-overview__image-block">
              <img
                src={overviewImg}
                alt="Instrumentation and Monitoring Works"
                className="im-overview__image"
              />
              <div className="im-overview__stat-card">
                <span className="im-overview__stat-number">2+</span>
                <span className="im-overview__stat-text">MONITORING<br />PROJECTS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`im-methods ${isVisible["methods"] ? "im-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="im-methods__inner">
          <div className="im-section-label">
            <span className="im-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="im-section-title">
            MONITORING
            <br />
            <span className="im-text-accent">METHODS</span>
          </h2>
          <p className="im-methods__subtitle">
            From lateral ground movement to structural response — we deploy a full suite
            of instrumentation tailored to each project's monitoring requirements.
          </p>
          <div className="im-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="im-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="im-method-card__number">{method.number}</div>
                <div className="im-method-card__icon">{method.icon}</div>
                <h3 className="im-method-card__title">{method.title}</h3>
                <p className="im-method-card__desc">{method.desc}</p>
                <div className="im-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`im-process ${isVisible["process"] ? "im-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="im-process__inner">
          <div className="im-section-label">
            <span className="im-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="im-section-title">
            OUR <span className="im-text-accent">PROCESS</span>
          </h2>
          <div className="im-process__steps">
            {[
              { step: "01", title: "Monitoring Plan & Design", desc: "Review of project risk areas and design of an instrumentation layout with defined alert thresholds." },
              { step: "02", title: "Instrument Installation", desc: "Installation of inclinometers, piezometers, settlement markers, and structural sensors at planned locations." },
              { step: "03", title: "Baseline Readings", desc: "Initial baseline data capture across all instruments prior to the start of construction activities." },
              { step: "04", title: "Continuous Monitoring", desc: "Scheduled or automated real-time readings throughout construction with threshold-triggered alerts." },
              { step: "05", title: "Reporting & Review", desc: "Periodic monitoring reports with trend analysis, shared with project stakeholders for informed decision-making." },
            ].map((item, i) => (
              <div key={i} className="im-process__step">
                <div className="im-process__step-num">{item.step}</div>
                <div className="im-process__step-connector" />
                <div className="im-process__step-content">
                  <h4 className="im-process__step-title">{item.title}</h4>
                  <p className="im-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`im-projects ${isVisible["projects"] ? "im-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="im-projects__inner">
          <div className="im-section-label">
            <span className="im-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="im-section-title">
            COMPLETED MONITORING
            <br />
            <span className="im-text-accent">PROJECTS</span>
          </h2>
          <div className="im-projects__grid">
            {[
              { title: "Instrumentation and Monitoring works at Phoenix road", client: "Poh Huat Engineering & Construction Pte Ltd" },
              { title: "Instrumentation and Monitoring works at Woodlands Ave 5", client: "Poh Huat Engineering & Construction Pte Ltd" },
            ].map((proj, i) => (
              <div key={i} className="im-project-card">
                <h4 className="im-project-card__title">{proj.title}</h4>
                <p className="im-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="im-projects__cta">
            <a href="/projects" className="im-btn im-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      

      {/* CTA Banner */}
      <section className="im-cta">
        <div className="im-cta__inner">
          <div className="im-cta__left">
            <p className="im-cta__label">READY TO START?</p>
            <h2 className="im-cta__title">
              NEED INSTRUMENTATION
              <br />
              <span className="im-text-accent">FOR YOUR PROJECT?</span>
            </h2>
            <p className="im-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project's monitoring requirements.
            </p>
          </div>
          <div className="im-cta__right">
            <a href="/contact-us" className="im-btn im-btn--primary im-btn--large">
              CONTACT US →
            </a>
            <div className="im-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InstrumentationMonitoring;